import {$type} from "../utils/di-container.js";

/**
 * @typedef _HttpError
 * {'server'|'client'} type
 * {any} [details]
 */

/**
 * @typedef {object} _HttpResponse
 * {object} [data]
 * {_HttpError} [error]
 * {Date} responseDate
 */

/**
 * @typedef {'GET'|'POST'|'PUT'|'DELETE'} _HttpMethod
 */

/**
 * Minimal HTTP request implementation that also adheres to auth-by API rules and structure.
 * Deals with AJAX boilerplate and also with request/response details, so that consumers of this
 *   can more easily and cleanly make auth-by requests and interpret responses.
 */
export class HttpService {

  /**
   * @param {_HttpMethod} method
   * @param {string} url
   * @param {Object.<string, string>} [params = {}]
   * @param {Object.<string, string>} [headers = {}]
   * @returns {Promise<_HttpResponse>}
   */
  async request(method, url, params = {}, headers = {}) {
    return new Promise(resolve => {
      const httpRequest = new XMLHttpRequest();

      let paramStr = Object.entries(params)
        .filter(([/**/, value]) => !!value)
        .map(([key, value]) => `${key}=${encodeURIComponent(/** @type {string} */ value)}`)
        .join('&');

      httpRequest.open(method, paramStr ? `${url}?${paramStr}` : url);

      // Set headers
      for (const [key, value] of Object.entries(headers)) {
        httpRequest.setRequestHeader(key, value);
      }

      httpRequest.onreadystatechange = () => {
        if (httpRequest.readyState >= 4) {
          const status = httpRequest.status;
          const statusText = httpRequest.statusText;
          let response = {};
          if (status < 300) {
            // 200 class - success
            let data;
            try {
              data = JSON.parse(httpRequest.responseText);
              if (data.error) {
                // auth-by error in body - this is what we mostly use in auth-by APIs to avoid companies receiving badge-related errors
                response.error = {
                  type: 'server',
                  enabledFeatures: data.enabledFeatures,
                  code: data.error,
                  details: data.message
                };
              } else {
                // success
                response.data = data;
              }
            } catch (e) {
              response.error = {
                type: 'client',
                code: '001',
                details: 'Error parsing response'
              };
            }
          } else if (status < 400) {
            // 300 class - redirection
            response.error = {
              type: 'client',
              code: '002',
              details: 'Redirection not supported'
            };
          } else if (status < 500) {
            // 400 class - client error
            response.error = {
              type: 'client',
              code: '040',
              details: `HTTP client error: ${status} / ${statusText}`
            };
          } else {
            // 500 class - server error
            response.error = {
              type: 'server',
              code: '050',
              details: `HTTP server error: ${status} / ${statusText}`
            };
          }

          let rawDate;
          try {
            rawDate = httpRequest.getResponseHeader('Date');
          } catch (e) {
            // ignore
          }
          // use response "date" header, or client's date as a fallback
          response.responseDate = rawDate ? new Date(rawDate) : new Date();
          resolve(response);
        }
      };
      httpRequest.send();
    });
  }

  /**
   * @param {string} url
   * @param {Object.<string, string>} [params = {}]
   * @param {Object.<string, string>} [headers = {}]
   * @returns {Promise<_HttpResponse>}
   */
  async get(url, params = {}, headers = {}) {
    return await this.request('GET', url, params, headers);
  }

}

HttpService[$type] = Symbol('HttpService');
